import * as React from "react";
import { useEffect } from "react";
import {
  PopoverSurface,
  PopoverTrigger,
  Popover,
  makeStyles,
  Title3,
  Text,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Divider,
  Caption1,
  Button,
  OverlayDrawer,
  DrawerBody,
  Field,
  Input,
  mergeClasses
} from "@fluentui/react-components";
import PropTypes from "prop-types";
import configs from "../utils/configs";
import bwipjs from "@bwip-js/browser";

import { Info24Filled, CopyRegular, ArrowDownloadFilled, Dismiss20Regular } from "@fluentui/react-icons";
import useBreakpoints from "./hooks/useBreakpoints";
import { hubUrl } from "../utils/phoenix-utils";
import notificationsCenter from "../singleton/NotificationsCenter";

const useStyles = makeStyles({
  infoButtonContainer: {
    "@media (min-width: 992px)": {
      position: "fixed",
      bottom: "20px",
      left: "30px"
    }
  },
  entered: {
    right: "70px",
    "@media (min-width: 992px)": {
      right: "unset"
    }
  },
  infoButton: {
    display: "flex",
    width: "40px",
    minWidth: "40px",
    maxWidth: "40px",
    border: "none",
    height: "40px",
    padding: "var(--Vertical-SNudge, 6px) var(--Horizontal-SNudge, 6px)",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: "0",
    borderRadius: "4px",
    background: "rgba(22, 24, 30, 0.60)",
    cursor: "pointer",
    "& svg": {
      color: "#fff",
      fill: "#fff"
    }
  },
  buttonActive: {
    backgroundColor: "#16181E!important"
  },
  titleContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between"
  },
  headerContainer: {
    width: "100%",
    display: "flex",
    alignItems: "start"
  },
  titleContainerText: {
    wordBreak: "break-word"
    // display: "-webkit-box",
    // "-webkit-line-clamp": 2,
    // "-webkit-box-orient": "vertical",
    // overflow: "hidden"
  },
  scenePopover: {
    display: "flex",
    width: "300px",
    top: "-7px!important"
  },
  scenePopoverContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%"
  },
  sceneImage: {
    display: "flex",
    height: "144px",
    justifyContent: "center",
    alignItems: "center",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sceneName: {
    display: "flex",
    height: "29px",
    flexDirection: "column",
    justifyContent: "center"
  },
  sceneNameText: {
    color: "var(--White, #FFF)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal"
  },
  accordionHeader: {
    "& .fui-AccordionHeader__button": {
      padding: "0px"
    }
  },
  accordionHeaderText: {
    color: "var(--White, #FFF)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "var(--Line-height-300, 20px)"
  },
  inviteContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px"
  },
  qrCodeContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    gap: "6px"
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "var(--Font-size-100, 10px)"
  },
  accordionPanel: {
    margin: 0,
    // display: "-webkit-box",
    // "-webkit-line-clamp": 3,
    // "-webkit-box-orient": "vertical",
    // overflow: "hidden",
    marginBottom: "12px"
  },
  inputVr: {
    width: "100%",
    backgroundColor: "rgba(245, 245, 245, 0.1)",
    borderBottom: "none",
    "& input": {
      color: "#fff",
      cursor: "default"
    },
    "&:after": {
      borderBottom: "none"
    }
  },
  inviteField: {
    "& label": {
      color: " var(--Gray-4, #BDBDBD)",
      fontSize: "var(--Font-size-200, 12px)",
      fontStyle: "normal",
      fontWeight: "400",
      lineHeight: "var(--Line-height-200, 16px)"
    }
  },
  menuPopover: {
    top: "-15px!important"
  }
});

const SceneDetails = ({ room, onClose, isMobile = false }) => {
  const shortUrl = `https://${configs.SHORTLINK_DOMAIN}`;
  const url = `${shortUrl}/${room?.hub_id}`;

  let embedText = null;
  const store = window.APP.store;
  const embedToken = room.embed_token || store.getEmbedTokenForHub(room);
  if (embedToken) {
    const embedUrl = hubUrl(room.hub_id, { embed_token: embedToken });
    embedText = `<iframe src="${embedUrl}" style="width: 1024px; height: 768px;" allow="microphone; camera; vr; speaker;"></iframe>`;
  }

  const copyUrl = url => {
    navigator.clipboard
      .writeText(url)
      .then(() => {})
      .catch(err => {
        console.error("Failed to copy: ", err);
      });
  };

  const qrCanvasRef = React.useRef(null); // Ref to the QR code canvas
  const copyQRCode = () => {
    copyUrl(url);
    notificationsCenter.success("QR Code has been copied");
  };

  const downloadQRCode = () => {
    const canvas = qrCanvasRef.current;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "qrcode.png";
    link.click();
  };

  const [openItems, setOpenItems] = React.useState(["1"]);
  const handleToggle = async (event, data) => {
    setOpenItems(data.openItems);
    await new Promise((resolve, reject) => setTimeout(resolve));

    if (data.openItems.includes("1") && qrCanvasRef.current) {
      generateQRCode();
    }
  };

  const generateQRCode = () => {
    bwipjs.toCanvas(qrCanvasRef.current, {
      bcid: "qrcode",
      text: url,
      scaleX: isMobile ? 3 : 2,
      scaleY: isMobile ? 3 : 2,
      includetext: false,
      textxalign: "center",
      paddingwidth: 3,
      paddingheight: 3,
      backgroundcolor: "#fff"
    });
    // setQRCanvas(canvas);
  };

  useEffect(() => {
    if (qrCanvasRef.current) {
      generateQRCode();
    }
  }, [room?.scene?.slug]);

  const copyMetaverseLink = () => {
    copyUrl(url);
    notificationsCenter.success("Verse link has been copied");
  };

  const copyEmbedLink = () => {
    copyUrl(embedText);
    notificationsCenter.success("Embed code has been copied");
  };

  const styles = useStyles();

  const [additionalStyles, setAdditionalStyles] = React.useState({});
  useEffect(() => {
    const handleResize = () => {
      const containerHeight = window.innerHeight - 210;
      const contentHeight = document.querySelector("#room-description-content")?.scrollHeight;

      if (!isMobile) {
        setAdditionalStyles({
          maxHeight: `calc(100vh - 210px)`,
          overflowY: contentHeight > containerHeight ? "auto" : "hidden"
        });
      } else {
        setAdditionalStyles({});
      }
    };
    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobile, window.innerHeight, room?.description]);

  return (
    <div className={styles.scenePopoverContainer} id="room-description-content">
      <div style={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button style={{ maxHeight: "32px" }} appearance="subtle" icon={<Dismiss20Regular />} onClick={onClose} />
      </div>
      <div style={{ ...additionalStyles, width: "100%", paddingLeft: "10px", paddingRight: "10px" }}>
        <div className={styles.headerContainer}>
          <div className={styles.titleContainer}>
            <Title3 className={styles.titleContainerText}>{room?.name}</Title3>
          </div>
        </div>

        <div style={{ width: "100%" }}>
          {room.description && (
            <>
              <Accordion collapsible multiple defaultOpenItems="1">
                <AccordionItem value="1">
                  <AccordionHeader className={styles.accordionHeader}>
                    <span className={styles.accordionHeaderText}>Description</span>
                  </AccordionHeader>
                  <AccordionPanel
                    style={{ marginBottom: "12px", paddingLeft: "4px", paddingRight: "4px" }}
                    className={styles.accordionPanel}
                  >
                    {room?.description}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
              <Divider />
            </>
          )}
          <Accordion style={{ width: "100%" }} collapsible multiple openItems={openItems} onToggle={handleToggle}>
            <AccordionItem value="1" style={{ width: "100%" }}>
              <AccordionHeader className={styles.accordionHeader}>
                <span className={styles.accordionHeaderText}>Invite</span>
              </AccordionHeader>
              <AccordionPanel style={{ width: "100%" }} className={styles.accordionPanel}>
                <div style={{ width: "100%" }} className={styles.inviteContainer}>
                  <Text size={"small"}>
                    <Caption1 style={{ color: "#828282" }}>Scan to access this Verse</Caption1>
                  </Text>

                  <div className={styles.qrCodeContainer}>
                    <canvas
                      id="qr-info"
                      ref={qrCanvasRef}
                      style={{ borderRadius: "4px", border: "1px solid #BDBDBD" }}
                    ></canvas>
                    <div className={styles.buttonsContainer}>
                      <Button appearance="outline" icon={<CopyRegular />} onClick={copyQRCode} />
                      <Button appearance="primary" icon={<ArrowDownloadFilled />} onClick={downloadQRCode} />
                    </div>
                  </div>
                  <Field className={styles.inviteField} label={"Verse Link"} style={{ width: "100%" }}>
                    <Input
                      readOnly
                      value={url}
                      contentAfter={<CopyRegular style={{ cursor: "pointer" }} onClick={copyMetaverseLink} />}
                      appearance="filled-lighter"
                      className={styles.inputVr}
                    />
                  </Field>

                  {embedToken && (
                    <Field className={styles.inviteField} label={"Embed Code"} style={{ width: "100%" }}>
                      <Input
                        readOnly
                        value={embedText}
                        contentAfter={<CopyRegular style={{ cursor: "pointer" }} onClick={copyEmbedLink} />}
                        appearance="filled-lighter"
                        className={styles.inputVr}
                      />
                    </Field>
                  )}
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export const ButtonInfo = ({ room, open, onOpenChange, entered }) => {
  const styles = useStyles();
  const handleOpenChange = (e, data) => onOpenChange(data.open || false);

  const breakpoint = useBreakpoints();
  const isMobile = ["xs", "sm"].includes(breakpoint);

  return (
    <div className={mergeClasses(styles.infoButtonContainer, entered && styles.entered)}>
      {!isMobile && (
        <Popover open={open} positioning={"above-start"} withArrow onOpenChange={handleOpenChange}>
          <PopoverTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              className={!open ? styles.infoButton : mergeClasses(styles.infoButton, styles.buttonActive)}
            >
              <Info24Filled />
            </Button>
          </PopoverTrigger>

          <PopoverSurface tabIndex={-1} className={styles.scenePopover}>
            <SceneDetails room={room} onClose={() => onOpenChange(false)} />
          </PopoverSurface>
        </Popover>
      )}
      {isMobile && (
        <>
          <div className={styles.infoButton} onClick={() => onOpenChange(true)}>
            <Info24Filled />
          </div>
          <OverlayDrawer position="bottom" size="full" open={open}>
            <DrawerBody style={{ padding: "40px 30px" }}>
              <SceneDetails isMobile={true} room={room} onClose={() => onOpenChange(false)} />
            </DrawerBody>
          </OverlayDrawer>
        </>
      )}
    </div>
  );
};

SceneDetails.propTypes = {
  isMobile: PropTypes.bool,
  room: PropTypes.object,
  onClose: PropTypes.func
};

ButtonInfo.propTypes = {
  room: PropTypes.object,
  scene: PropTypes.object,
  entered: PropTypes.bool
};
